<template>
  <div v-if="presentation" class="exact-game-page" :class="{ 'exact-arcade-page':presentation.arcade, 'exact-sport-page':presentation.key === 'sport-main' }">
    <section v-if="!presentation.arcade" class="container exact-game-stage">
      <div class="exact-game-art"><img v-if="presentation.key === 'sport-main'" class="exact-game-backdrop" :src="referenceAsset('sport-main',17)" alt="" /><img class="exact-game-person" :key="artwork" :style="{width:`${presentation.width}px`,height:`${presentation.height}px`}" :src="artwork" :alt="`${routeTitle[0]}场馆展示`" /></div>
      <div class="exact-game-copy">
        <img v-if="activeBrand" class="exact-game-title" :src="venueBrandAsset(activeBrand.title)" :alt="activeVenue.name" /><div v-else-if="activeVenue" class="exact-game-title-text"><span>{{ activeVenue.name }}</span><small>{{ routeTitle[0] }}</small></div><img v-else class="exact-game-title" :src="referenceAsset(presentation.key,presentation.title)" :alt="routeTitle[0]" />
        <div v-if="rate !== null" class="exact-rebate"><span>高达 <b>{{ rate }}<small>%</small></b></span><em>无限返水</em></div>
        <div v-if="rate === null" class="exact-rebate-ghost" aria-hidden="true"></div>
        <p>{{ activeBrand?.description || presentation.description }}</p>
        <img v-if="activeBrand?.symbols || presentation.symbols" class="exact-game-symbols" :src="activeBrand?.symbols ? venueBrandAsset(activeBrand.symbols) : referenceAsset(presentation.key,presentation.symbols)" alt="支持的游戏类型" />
        <div class="exact-provider-tabs" role="tablist" aria-label="场馆选择"><button v-for="(venue,index) in displayRouteCards" :key="venue.id" role="tab" :aria-label="venue.name" :aria-selected="activeIndex === index" :class="{ active:activeIndex === index }" @click="selectVenue(venue)"><span class="exact-provider-logo"><img v-if="venue.logoUrl || venue.iconUrl" :src="venue.logoUrl || venue.iconUrl" alt="" /><img v-else-if="resolveVenueBrand(presentation.key,venue)?.logo" :src="venueBrandAsset(resolveVenueBrand(presentation.key,venue).logo)" alt="" /><b v-else>{{ providerMark(venue.name) }}</b></span><span>{{ venue.name }}</span></button></div>
        <p v-if="routeLoading" role="status">正在加载场馆…</p><p v-else-if="!displayRouteCards.length">当前分类暂无可用场馆</p>
        <button class="exact-enter" :disabled="routeLoading || !activeVenue" @click="openVenueGames(activeVenue)">进入游戏</button>
      </div>
    </section>
    <template v-else>
      <section class="container exact-arcade-hero"><img :src="referenceAsset(presentation.key,presentation.title)" :alt="routeTitle[0]" /><img :src="referenceAsset(presentation.key,presentation.artwork[0])" alt="" /></section>
      <section class="container exact-arcade-lobby">
        <div class="exact-arcade-providers" role="tablist" aria-label="场馆选择"><button v-for="(venue,index) in displayRouteCards" :key="venue.id" role="tab" :aria-selected="activeIndex===index" :class="{ active:activeIndex===index }" @click="selectVenue(venue)"><span>{{ providerMark(venue.name) }}</span>{{ venue.name }}<small v-if="/^\d+(?:\.\d+)?$/.test(String(venue.rebateRate ?? ''))">高达{{ venue.rebateRate }}% 无限返水</small></button></div>
        <form class="exact-arcade-toolbar" @submit.prevent="loadGames(1)"><div><button v-for="tab in gameFilters" :key="tab.value" type="button" :class="{ active:filter===tab.value }" :disabled="tab.unavailable" :title="tab.unavailable ? '当前场馆未提供该分类' : undefined" @click="selectFilter(tab.value)">{{ tab.label }}</button></div><label><span class="sr-only">搜索游戏</span><input v-model.trim="keyword" placeholder="搜索游戏" maxlength="100" /><button aria-label="搜索游戏">⌕</button></label></form>
        <p v-if="loading" class="exact-lobby-state" role="status">正在加载游戏…</p><ErrorNotice v-else-if="error" class="exact-lobby-state" :message="error"><button @click="loadGames(page)">重新加载</button></ErrorNotice>
        <div v-else-if="games.length" class="exact-arcade-grid"><article v-for="game in games" :key="game.id"><button class="exact-game-card" @click="launchVenueGame(game)"><img v-if="game.iconUrl" :src="game.iconUrl" :alt="game.name" loading="lazy" /><span v-else class="exact-game-placeholder">{{ game.name }}</span><strong>{{ game.name }}</strong><small>{{ activeVenue?.name }}</small><em>开始游戏</em></button><button class="exact-favorite" :aria-label="`${favoriteGameIds.has(game.id)?'取消收藏':'收藏'} ${game.name}`" :aria-pressed="favoriteGameIds.has(game.id)" :disabled="favoritePending.size>0" @click="toggleFavorite(game)">{{ favoriteGameIds.has(game.id)?'♥':'♡' }}</button></article></div>
        <p v-else class="exact-lobby-state">暂无符合条件的游戏</p>
        <nav v-if="total>pageSize" class="exact-game-pages" aria-label="游戏分页"><button :disabled="page<=1||loading" @click="loadGames(page-1)">‹</button><span>{{ page }} / {{ Math.ceil(total/pageSize) }}</span><button :disabled="page*pageSize>=total||loading" @click="loadGames(page+1)">›</button></nav>
      </section>
    </template>
  </div>
</template>
<script setup>
import ErrorNotice from '../feedback/ErrorNotice.vue'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useAppContext } from '../../composables/appContext'
import { gamePresentations, referenceAsset } from '../../config/referencePresentation.js'
import { webApi } from '../../services/webApi.js'
import { loadAllGameFavorites, favoriteGamePage } from '../../utils/gameFavorites.js'
import { resolveVenueBrand, venueBrandAsset } from '../../config/venueBrandPresentation.js'
const { currentPath, displayRouteCards, selectedVenue, routeLoading, routeTitle, openVenueGames, isLoggedIn, openAuth, launchVenueGame, favoriteGameIds, favoritePending, toggleFavorite } = useAppContext()
const presentation = computed(()=>gamePresentations[currentPath.value])
const activeIndex = computed(()=>Math.max(0,displayRouteCards.value.findIndex(v=>v.id===selectedVenue.value?.id)))
const activeVenue = computed(()=>displayRouteCards.value[activeIndex.value] || null)
const activeBrand=computed(()=>resolveVenueBrand(presentation.value.key,activeVenue.value))
const artwork = computed(()=>activeBrand.value?.artwork ? venueBrandAsset(activeBrand.value.artwork) : referenceAsset(presentation.value.key,presentation.value.artwork[activeIndex.value%presentation.value.artwork.length]))
const rate = computed(()=>{const value=activeVenue.value?.rebateRate;return isLoggedIn.value && /^\d+(?:\.\d+)?$/.test(String(value ?? ''))?value:null})
const keyword=ref(''), filter=ref('all'), games=ref([]), loading=ref(false), error=ref(''), page=ref(1), total=ref(0), pageSize=20
let sequence=0
const gameFilters=[{label:'全部游戏',value:'all'},{label:'热门游戏',value:'hot'},{label:'最新游戏',value:'new',unavailable:true},{label:'我的收藏',value:'favorites'},{label:'最近游戏',value:'recent',unavailable:true}]
function providerMark(name='') { return (name.match(/[A-Za-z]+/)?.[0] || name.slice(0,2)).toUpperCase() }
function selectVenue(venue) { selectedVenue.value=venue }
function selectFilter(value) {
  if (['hot', 'favorites'].includes(value) && !isLoggedIn.value) return openAuth('login')
  filter.value = value
  void loadGames(1)
}
async function loadGames(nextPage=1) {
  const ticket=++sequence; games.value=[]; total.value=0; error.value=''; loading.value=false; page.value=nextPage
  if(!presentation.value?.arcade || !activeVenue.value) return
  if (filter.value !== 'all' && !isLoggedIn.value) return openAuth('login')
  loading.value=true
  try {
    let result
    if(filter.value==='hot'){
      const discovery=await webApi.gameDiscovery()
      const query=keyword.value.toLowerCase()
      const matches=(discovery?.hotGames || []).filter(game=>String(game.venueId)===String(activeVenue.value.id) && `${game.name || ''} ${game.gameCode || ''}`.toLowerCase().includes(query))
      result={data:matches.slice((nextPage-1)*pageSize,nextPage*pageSize),total:matches.length}
    } else if (filter.value === 'favorites') {
      // 收藏接口当前仅接收分页，完整读取后再按本页场馆、关键词筛选。
      const favorites = await loadAllGameFavorites(webApi.favoriteGames)
      result = favoriteGamePage(favorites.filter(game => String(game.venueId) === String(activeVenue.value.id)), keyword.value, nextPage, pageSize)
    } else {
      result = await webApi.games({ venueId: activeVenue.value.id, keyword: keyword.value, pageNumber: nextPage, pageSize })
    }
    if(ticket!==sequence)return
    const rows=result?.data || []
    games.value=rows
    total.value=Number(result?.total || 0)
  }catch(e){if(ticket===sequence)error.value=e.message || '游戏加载失败'}
  finally{if(ticket===sequence)loading.value=false}
}
watch(favoriteGameIds,()=>{if(filter.value==='favorites')void loadGames(page.value)})
watch([currentPath,()=>activeVenue.value?.id,isLoggedIn],()=>{keyword.value='';filter.value='all';void loadGames(1)},{immediate:true})
onBeforeUnmount(()=>{sequence++})
</script>

<style scoped>
.exact-sport-page .exact-provider-tabs {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  align-items: start;
  gap: 12px;
  width: min(100%, 372px);
  margin-top: 36px;
}

.exact-sport-page .exact-provider-tabs > button {
  min-width: 0;
  gap: 8px;
  border-radius: 14px;
  color: #556982;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.exact-sport-page .exact-provider-logo {
  position: relative;
  box-sizing: border-box;
  width: min(100%, 68px);
  height: 64px;
  border: 1px solid #dce5f1;
  border-radius: 14px;
  background: #ffffffd9;
  box-shadow: 0 3px 8px #3b609a0a;
  transition: border-color 160ms ease, background-color 160ms ease, transform 160ms ease;
}

.exact-sport-page .exact-provider-logo b {
  color: #526d91;
  font-size: 21px;
  font-weight: 600;
}

.exact-sport-page .exact-provider-logo > img {
  width: 42px;
  height: 42px;
  filter: none;
  opacity: 1;
}

.exact-sport-page .exact-provider-tabs > button:hover .exact-provider-logo {
  border-color: #86b9e6;
  background: #fff;
  transform: translateY(-2px);
}

.exact-sport-page .exact-provider-tabs > button.active {
  color: #176fba;
  font-weight: 600;
}

.exact-sport-page .exact-provider-tabs .active .exact-provider-logo {
  border-color: #409be5;
  background: #eaf5ff;
  box-shadow: 0 3px 10px #278bda1a;
}

.exact-sport-page .exact-provider-tabs .active .exact-provider-logo b {
  color: #176fba;
}

.exact-sport-page .exact-provider-tabs .active .exact-provider-logo::after {
  position: absolute;
  top: 4px;
  right: 4px;
  display: grid;
  place-items: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  color: #fff;
  background: #258bda;
  font: 600 10px/1 sans-serif;
  content: '✓';
}

.exact-sport-page .exact-provider-tabs > button:focus-visible {
  outline: 2px solid #258bda;
  outline-offset: 4px;
}

.exact-sport-page .exact-game-copy > .exact-enter {
  margin-top: 28px;
}

@media (max-width: 767px) {
  .exact-sport-page .exact-provider-tabs { margin-top: 24px; gap: 10px; }
  .exact-sport-page .exact-provider-tabs > button { font-size: 12px; }
  .exact-sport-page .exact-provider-logo { height: 60px; }
}

@media (prefers-reduced-motion: reduce) {
  .exact-sport-page .exact-provider-logo { transition: none; }
  .exact-sport-page .exact-provider-tabs > button:hover .exact-provider-logo { transform: none; }
}
</style>
